/**
 * Downscaling before upload. 2400px on the long edge keeps an X-ray label and a
 * lab report line readable while cutting size roughly tenfold; below ~1600px
 * that text starts to go. PDFs pass through untouched.
 */
const MAX_EDGE = 2400
const QUALITY = 0.82

export interface PreparedFile {
  blob: Blob
  filename: string
  contentType: string
}

export async function prepareForUpload(
  blob: Blob,
  filename: string,
  contentType: string,
): Promise<PreparedFile> {
  if (!contentType.startsWith('image/') || contentType === 'image/heic') {
    return { blob, filename, contentType }
  }
  try {
    // createImageBitmap applies EXIF orientation to the pixels, so a sideways
    // photographed film comes out upright and the metadata can be dropped.
    const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' })
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    if (scale === 1 && blob.size < 1_500_000) {
      bitmap.close()
      return { blob, filename, contentType }
    }
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    const context = canvas.getContext('2d')
    if (!context) return { blob, filename, contentType }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    const encoded = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY),
    )
    if (!encoded || encoded.size >= blob.size) return { blob, filename, contentType }
    // Re-encoding through a canvas drops every EXIF tag, including GPS — which
    // has no business being attached to a clinical image.
    return {
      blob: encoded,
      filename: filename.replace(/\.[^.]+$/, '') + '.jpg',
      contentType: 'image/jpeg',
    }
  } catch {
    // A browser that cannot decode it still gets to upload it.
    return { blob, filename, contentType }
  }
}
